import React from 'react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MapPin, Calendar, Heart } from 'lucide-react';
import Card from '../common/Card';
import Button from '../common/Button';
import { getDepartureDday, ddayToneClass } from './obituaryUtils';

const ObituaryCard = ({
  obituary,
  onFavoriteToggle,
  onViewDetail,
  onSendWreath,
  showFavoriteButton = true,
  className = ''
}) => {
  const {
    id,
    deceasedName,
    relationship,
    funeralHome,
    funeralAddress,
    funeralRoom,
    departureDate,
    departureTime,
    chiefMourner,
    chiefMournerRelationship,
    isFavorite
  } = obituary;

  // 발인일시 포맷팅
  const formattedDepartureDate = format(
    new Date(departureDate),
    'yyyy년 MM월 dd일 (E)',
    { locale: ko }
  );

  const dday = getDepartureDday(departureDate);

  // 카드 키보드 활성화 (Enter / Space)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onViewDetail?.();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label={`故 ${deceasedName} 부고 상세보기`}
      className="rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
    >
    <Card
      className={`relative ${className}`}
      hover={true}
      onClick={onViewDetail}
    >
      {/* 발인 D-day 뱃지 */}
      {dday && (
        <span
          className={`absolute top-4 left-4 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${ddayToneClass(dday.tone)}`}
        >
          {dday.label}
        </span>
      )}

      {/* 즐겨찾기 버튼 */}
      {showFavoriteButton && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onFavoriteToggle?.(id);
          }}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-accent-100 transition-colors"
          aria-label={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
        >
          <Heart
            size={20}
            className={isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}
          />
        </button>
      )}

      {/* 고인 정보 */}
      <div className="mb-4 pr-10 pt-6">
        <h3 className="text-xl font-bold text-primary-900 mb-1 truncate">
          故 {deceasedName}
        </h3>
        <p className="text-sm text-gray-600 truncate">
          {chiefMourner} {chiefMournerRelationship}의 {relationship}
        </p>
      </div>

      {/* 장례식장 정보 */}
      <div className="space-y-2 mb-4">
        <div className="flex items-start gap-2">
          <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
          <div className="text-sm min-w-0">
            <p className="font-semibold text-gray-900 truncate">{funeralHome}</p>
            <p className="text-gray-600 truncate">{funeralAddress} {funeralRoom}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-500 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-gray-900">
              <span className="font-semibold">발인: </span>
              {formattedDepartureDate} {departureTime}
            </p>
          </div>
        </div>
      </div>

      {/* 버튼 영역 */}
      <div className="flex gap-2 mt-4">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetail?.();
          }}
        >
          상세보기
        </Button>
        <Button
          variant="primary"
          size="sm"
          className="flex-1"
          onClick={(e) => {
            e.stopPropagation();
            onSendWreath?.(id, obituary);
          }}
        >
          화환 보내기
        </Button>
      </div>
    </Card>
    </div>
  );
};

export default ObituaryCard;
